import { ResourceHeading2 } from 'components/theme/Typography/ResourceTypography';
import { AvailableTimeList } from 'features/GuestReschedule/components/AvailableTimeList';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import moment, { Moment } from 'moment';
import { DateSelector } from 'components/DatePicker/DateSelector';
import {
  RescheduleMessage,
  Label,
} from 'features/GuestReschedule/components/Layout';
import InfoCircleIcon from 'assets/bx-info-circle';
import useGuestReschedule from 'features/GuestReschedule/hooks/useGuestReschedule';
import { useGuestRescheduleSessions } from 'features/GuestReschedule/hooks/useGuestRescheduleSessions';
import { DaySchedule } from 'features/GuestReschedule/types';
import { ResourcesDropdown } from 'features/GuestReschedule/components/ResourcesDropdown';
import { AvailableTimeListSkeleton } from 'features/GuestReschedule/components/AvailableTimeSkeleton';

interface TimeSelectorProps {
  readonly times: DaySchedule[];
  readonly defaultResourceId: string;
  readonly resources: { label: string; value: string }[];
  readonly noSessionsAvailable: boolean;
  readonly selectedSlotId: string | null;
  readonly onSelectSlot: (value: string) => void;
  readonly isLoading: boolean;
}

export const TimeSelector = ({
  times,
  defaultResourceId,
  resources,
  noSessionsAvailable,
  selectedSlotId,
  onSelectSlot,
  isLoading,
}: TimeSelectorProps) => {
  const { t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const {
    bookingSelected,
    setSelectedCalendarDate,
    selectedCalendarDate,
    setSelectedResourceId,
    selectedResourceId,
  } = useGuestReschedule();

  const [calendarMonth, setCalendarMonth] = useState<Moment>(moment());
  const calendarFrom = calendarMonth
    .clone()
    .startOf('month')
    .format('YYYY-MM-DD');

  const calendarTo = calendarMonth.clone().endOf('month').format('YYYY-MM-DD');

  const { sessions: calendarSessions } = useGuestRescheduleSessions(
    bookingSelected?.bookingId,
    calendarFrom,
    calendarTo,
  );

  const todayStr = moment().format('YYYY-MM-DD');

  const calendarDates = calendarSessions.map((s) => s.date);

  const availableDates = calendarDates.includes(todayStr)
    ? calendarDates
    : [todayStr, ...calendarDates];

  const handleDateSelectorFocusChange = ({ focused }: { focused: boolean }) =>
    setShowDatePicker(focused);

  const handleMonthChange = (date: Moment | null) => {
    if (!date) {
      return;
    }

    setCalendarMonth(date);
  };

  return (
    <Wrapper>
      <Header>
        <ResourceHeading2 fontSize="21px" className="times">
          {t('availableTimes')}
        </ResourceHeading2>
        <AvailableTimesWrapper>
          {resources.length > 1 && (
            <ResourcesDropdown
              selectedResourceId={selectedResourceId || defaultResourceId}
              options={resources}
              onChange={(r) => setSelectedResourceId(r.value)}
            />
          )}
          <DateSelector
            date={selectedCalendarDate || moment().toISOString()}
            showDatePicker={showDatePicker}
            onFocusChange={handleDateSelectorFocusChange}
            availableDates={availableDates}
            maxWidth="150px"
            onMonthChange={handleMonthChange}
            onChange={(date) => setSelectedCalendarDate(date)}
            keepOpenOnDateSelect={false}
          />
        </AvailableTimesWrapper>
      </Header>
      <TimesSelectorWrapper>
        {isLoading ? (
          <AvailableTimeListSkeleton />
        ) : (
          times.map((day) => (
            <AvailableTimeList
              key={day.date}
              date={day.date}
              slots={day.slots}
              selectedSlotId={selectedSlotId}
              onSelectSlot={onSelectSlot}
            />
          ))
        )}
        {noSessionsAvailable && !isLoading && (
          <RescheduleMessage>
            <InfoCircleIcon
              scale={1}
              color="var(--way-colors-primaryTextColor)"
            />
            <Label>{t('noOtherSessionsAvailableForThisExperience')}</Label>
          </RescheduleMessage>
        )}
      </TimesSelectorWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const AvailableTimesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimesSelectorWrapper = styled.div``;
